import React, { useEffect, useCallback, useRef } from 'react';
import 'moment/locale/da.js';
import Flatpickr from 'react-flatpickr';
import 'flatpickr/dist/themes/material_blue.css';
import CalendarLine from './CalendarLine';
import './Calendar.css';
import './custom-datepicker.css';
import { useSelector, useDispatch } from 'react-redux';
import { getAllPlans, setStartTime, setEndTime, showDeleteBox, deletePlannedItem } from '../../store/PlannedItemStore';
import { setStartThunk, setHighLight, setNumberOfDaysThunk, setTeamToShow } from '../../store/CalendarStore';
import * as EventStore from '../../store/EventStore';
import moment from 'moment';
import { Day } from './Day';


const Calendar = () => {
    const dispatch = useDispatch();

    const calendarState = useSelector((state) => state.calendarState);
    const plannedItemState = useSelector((state) => state.plannedItemState);
    const teamState = useSelector((state) => state.teamState);
    const eventState = useSelector((state) => state[EventStore.reducerName]);
    const teamToShow = calendarState.teamToShow;
    const numberOfDays = calendarState.numberOfDays;
    if (numberOfDays == undefined) {
        console.error('numberOfDays is undefined i Calnedar.js');
        return null;        
    }

    const headerRef = useRef(null);
    const bodyRef = useRef(null);

    useEffect(() => {
        dispatch(setStartThunk(moment().startOf('day').valueOf()));
        dispatch(getAllPlans());
        dispatch(setStartTime(moment().set('hours', 7).set('minutes', 0).valueOf()));
        dispatch(setEndTime(moment().set('hours', 15).set('minutes', 0).valueOf()));
        if (bodyRef.current && headerRef.current) {
            const scrollbarWidth = bodyRef.current.offsetWidth - bodyRef.current.clientWidth;
            headerRef.current.style.marginRight = `${scrollbarWidth}px`;
        }
    }, [dispatch]);

    // Memoized functions for dispatch logic
    const handleSetStart = useCallback(() => {
        dispatch(setStartThunk(moment().startOf('day').valueOf()));
    }, [dispatch]);

    const handleDateChange = useCallback((date) => {
        dispatch(setStartThunk(new Date(date[0]).valueOf()));
    }, [dispatch]);

    const handleChangeNumberOfDays = useCallback((nod) => {
        dispatch(setNumberOfDaysThunk(nod));
    }, [dispatch]);

    const handleHighlight = useCallback((id) => {
        dispatch(setHighLight(id));
    }, [dispatch]);

    const colorCodes = plannedItemState.colorCodes;
    const plans = plannedItemState.plans;
    let teams = teamState.teams;
    const events = eventState.events;
    const eventColor = plannedItemState.colorCodes.find((x) => x.name === 'Event');

    const pPlans = [];
    plans.forEach(x => {
        if(!pPlans[x.personId]) pPlans[x.personId] = [];
        pPlans[x.personId].push(x);
    });

    let hours = [];
    for(let i = 0; i < 24; i++){
        hours.push(moment(calendarState.days[0]).startOf('day').add(i, 'hours'));
    }

    const handleShowDeleteBox = (plan) => {
        dispatch(showDeleteBox({ id: plan.id, show: !plan.showDelete }));
    };
    const handleDeletePlannedItem = (id) => {
        dispatch(deletePlannedItem(id));
    };

    const createdPeople = {};
    const createUniqueKey = (p) => {
        let key = p.id;

        if(createdPeople.hasOwnProperty(p.id)){
            key = `${key}-${createdPeople[p.id]}`;
            createdPeople[p.id] = createdPeople[p.id] + 1;
        }else {
            createdPeople[p.id] = 1;
        }
        return key;
    }
    const renderLine = (p, i) => {
        const key = createUniqueKey(p);
        const dateView = numberOfDays !== 1;

        if(p.hidden){
            return;
        }
        return(
            <CalendarLine
                key={key}
                person={p}
                plans={pPlans[p.id] || []}
                events={events}
                eventColor={eventColor}
                days={dateView ? calendarState.days.map((day) => moment(day)) : hours}
                dayWidth={dateView ? 100 / numberOfDays : 100/24}
                granularity={dateView ? 'date' : 'hour'}
                highLightPersonId={calendarState.highLightPersonId}
                onClick={() => handleHighlight(p.id)}
                startTime={calendarState.days[0]}
                totalTime={numberOfDays}
                onToggleDeleteBox={handleShowDeleteBox}
                onDeletePlannedItem={handleDeletePlannedItem}
            />
        )
    };

    const renderLegend = () => {
        return colorCodes.map((cc) => {
            const style = {color: cc.hexColor};
            return (<li className='legend' key={cc.name} style={style}><p>{cc.name}</p></li>)
        });
    };

    const renderWeeks = (days) => {
        let arr = [];
        let lastWeek = null;
        let pointer = -1;

        days.forEach(d => {
            const curWeek = moment(d).week();
            if(curWeek === lastWeek){
                arr[pointer].count++;
            }
            else{
                pointer++;
                arr[pointer] = {week: curWeek, count: 1};
            }
            lastWeek = curWeek;
        });

        return arr.map((e) => {
            const style = { width: `${calendarState.dayWidth * e.count}%`};
            return <div className="dd-week" key={`${e.week}-${e.count}`} style={style}>UGE {e.week}</div>;
        } );
    };

    const renderMonth = (entry) => {
        const monthNames = ['Januar', 'Februar', 'Marts', 'April', 'Maj', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'December'];
        const style = { width: `${calendarState.dayWidth * entry.count}%`};
        return(
            <div className="dd-month" key={`${entry.month}-${entry.count}`} style={style}>{monthNames[entry.month]}</div>
        );
    };

    const renderMonths = (days) => {
        let arr = [];
        let lastMonth = null;
        let pointer = -1;

        days.forEach(d => {
            const curMonth = moment(d).month();
            if(curMonth === lastMonth){
                arr[pointer].count++;
            }
            else{
                pointer++;
                arr[pointer] = {month: curMonth, count: 1};
            }
            lastMonth = curMonth;
        });

        return arr.map((e) => renderMonth(e));
    };

    const renderHours = () => {
        const arr = [
            '00:00', '01:00', '02:00', '03:00', '04:00', '05:00',
            '06:00', '07:00', '08:00', '09:00', '10:00', '11:00',
            '12:00', '13:00', '14:00', '15:00', '16:00', '17:00',
            '18:00', '19:00', '20:00', '21:00', '22:00', '23:00'];
        return arr.map((i) => {
            const style = { width: `${90/24}%`};
            return <div className="dd-hour" key={i} style={style}>{i}</div>;
        } );
    };

    const personMap = () => {
        let persons = [];
        if(teams.length < 1) return null;

        const administration = { ...teams.find((x) => x.id === 8), teamLeader: { ...teams.find((x) => x.id === 8).teamLeader, leader: true } };

        const vejomraadet = { ...teams.find((x) => x.id === 9), teamLeader: { ...teams.find((x) => x.id === 9).teamLeader, leader: true } };
        const vej = teams.find((x) => x.id === 185);
        
        const gronvej = { ...teams.find((x) => x.id === 11), teamLeader: { ...teams.find((x) => x.id === 11).teamLeader, leader: true } };
        
        const komunaleEjendome = { ...teams.find((x) => x.id === 10), teamLeader: { ...teams.find((x) => x.id === 10).teamLeader, leader: true } };
        const team2 = teams.find((x) => x.id === 180);
        const team3 = teams.find((x) => x.id === 181);
        const tvear = teams.find((x) => x.id === 182);

        const vearkstedOgGenbrug = { ...teams.find((x) => x.id === 183), teamLeader: { ...teams.find((x) => x.id === 183).teamLeader, leader: true } };
        const genbrug = teams.find((x) => x.id === 184);

        const orderedTeams = [
            administration,
            vejomraadet,
            vej,
            gronvej,
            komunaleEjendome,
            team2,
            team3,
            // tvear,
            vearkstedOgGenbrug,
            genbrug
        ];

        teams = orderedTeams;

        for (let team of teams) {
            const hidden = teamToShow != 0 && teamToShow != team.id;
            const leaderWithProps = { ...team.teamLeader, leader: true, hidden };

            // Show leader if:
            // - Showing all teams and not already added
            // - OR filtering to a single team and this is the selected team
            if (
                (teamToShow == 0 && persons.filter(person => person.id === team.teamLeader.id).length === 0) ||
                (teamToShow != 0 && teamToShow == team.id)
            ) {
                persons.push(leaderWithProps);
            }

            team.members.forEach(member => {
                const memberWithHidden = { ...member, hidden };
                persons.push(memberWithHidden);
            });
        }

        return persons.map((p, i) => renderLine(p,i));
    };

    const createTeamOptions = () => {
        return teams && teams.map(t => {
            return <option key={t.id} value={t.id}> {t.name} </option>
        });
    };

    const changeTeamToShow = (id) => {
        dispatch(setTeamToShow(id));
    }

    const calendarChooser = () => {
        return(
            <div className="calender-chooser">
                <button className={'period-pick' + (numberOfDays === 180 ? ' current' : '')} onClick={() => handleChangeNumberOfDays(180)}>6 måneder</button>
                <button className={'period-pick' + (numberOfDays === 90  ? ' current' : '')} onClick={() => handleChangeNumberOfDays(90)}>3 måneder</button>
                <button className={'period-pick' + (numberOfDays === 30  ? ' current' : '')} onClick={() => handleChangeNumberOfDays(30)}>1 måned</button>
                <button className={'period-pick' + (numberOfDays === 14  ? ' current' : '')} onClick={() => handleChangeNumberOfDays(14)}>2 uger</button>
                <button className={'period-pick' + (numberOfDays === 1  ? ' current' : '')} onClick={() => handleChangeNumberOfDays(1)}>Dagsoverblik</button>
                <div className={'period-pick'}>|</div>
                <button className={'period-pick'} onClick={handleSetStart}>Nu</button>

                <div className="datepicker-container">
                    <Flatpickr 
                        value={moment(calendarState.start).format('YYYY-MM-DD')} 
                        options={{minDate: moment().format('YYYY-MM-DD')}}
                        onChange={handleDateChange}
                        className="date-picker-custom"/>
                </div>

                <span className="header-name"></span>
                    
                <div className="teampicker-container">
                    <select className="team-picker" onChange={e => changeTeamToShow(e.target.value)} value={teamToShow}>
                        <option key={0} value={0}> Alle teams </option>
                        {createTeamOptions()}
                    </select>
                </div>
            </div>
        );
    };

    const dayComponents = calendarState.days.map((day, i) => 
        <Day 
            key={i} 
            size={calendarState.dayWidth} 
            day={day} 
            onClick={setStartThunk}
        />
    );
    const monthComponents = renderMonths(calendarState.days);
    const weekComponents = renderWeeks(calendarState.days);
    const colorComponents = renderLegend();
    const lineComponents = personMap();
    const minutesSinceMidnight = moment().diff(moment().startOf('day'), 'minutes');

    return (
        <div className="content">
            {calendarChooser()}
            <div className='calendar'>
                <div className="header" ref={headerRef}>
                    <div className="row">
                        <span className="header-name"/>
                        {monthComponents}
                    </div>
                    <div className="row">
                        <span className="header-name"/>
                        {weekComponents}
                    </div>
                    <div className={'row c-' + (numberOfDays)}>
                        <span className="header-name"/>
                        {dayComponents}
                    </div>
                    {numberOfDays <= 1 ?
                        <div className={'row'}>
                            <span className="header-name"/>
                            {renderHours()}
                        </div> : null
                    }
                </div>
                <div className='calendar-body' ref={bodyRef}>
                    {lineComponents}
                    {numberOfDays <= 1 ? <div className='hour-line' style={{left: `calc(${minutesSinceMidnight / 1440 * 90}% + 10%)`}}/> : null }
                </div>
                <ul className="legend-container">
                    {colorComponents}
                </ul>
            </div>
        </div>
    );
}

export default Calendar;